import React from 'react';
import { Box, Skeleton } from '@mui/material';
import LotusLoader from './LotusLoader';

/**
 * PageLoader - A skeleton loader for lazy-loaded pages
 * 
 * Shows a skeleton layout that mimics the actual page structure
 * while the chunk is being loaded.
 * 
 * For simple loading states, consider using LotusLoader instead.
 */
export const PageLoader: React.FC = () => {
  return (
    <Box sx={{ maxWidth: '100%', p: 3 }}>
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" height={32} width="40%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={20} width="30%" />
        </Box>
        <Skeleton variant="rectangular" height={36} width={100} />
      </Box>

      {/* Filter section skeleton */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
      </Box>

      {/* Table skeleton */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 1 }} />
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={52}
            sx={{
              borderRadius: i === 0 ? '12px 12px 0 0' : i === 7 ? '0 0 12px 12px' : 0,
              mb: i < 7 ? 1 : 0,
            }}
          />
        ))}
      </Box>

      {/* Pagination skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="text" width={120} height={32} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * SimplePageLoader - A simple lotus loader for initial page loads
 * 
 * Shows a centered lotus bloom animation with optional text.
 * Use this for initial page loads or simple loading states.
 */
export const SimplePageLoader: React.FC<{ text?: string }> = ({ text = 'กำลังโหลด...' }) => {
  return (
    <Box sx={{ maxWidth: '100%', p: 3 }}>
      <LotusLoader size="large" variant="bloom" text={text} />
    </Box>
  );
};

export default PageLoader;

